import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import axios from "axios";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Building2, Plus, Trash2, Loader2, Users, ShieldCheck } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const VIS = [
  ["azienda", "Tutta l'azienda"],
  ["reparto", "Solo il proprio reparto"],
  ["proprio", "Solo le proprie ferie"],
];

export default function RepartiPage() {
  const { token, user } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [reparti, setReparti] = useState([]);
  const [users, setUsers] = useState([]);
  const [nuovo, setNuovo] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [r, u] = await Promise.all([
        axios.get(`${API}/api/reparti`, { headers }),
        axios.get(`${API}/api/users`, { headers }),
      ]);
      setReparti(r.data); setUsers(u.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel caricamento");
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const crea = async () => {
    if (!nuovo.trim()) return;
    try {
      await axios.post(`${API}/api/reparti`, { nome: nuovo.trim(), visibilita: "azienda" }, { headers });
      setNuovo(""); toast.success("Reparto creato"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const aggiorna = async (r, patch) => {
    try {
      await axios.put(`${API}/api/reparti/${r.id}`, { nome: r.nome, visibilita: r.visibilita, ...patch }, { headers });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const elimina = async (r) => {
    if (!window.confirm(`Eliminare il reparto "${r.nome}"? I dipendenti assegnati resteranno senza reparto.`)) return;
    try { await axios.delete(`${API}/api/reparti/${r.id}`, { headers }); toast.success("Reparto eliminato"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const assegna = async (u, payload) => {
    try { await axios.put(`${API}/api/reparti-assegna`, { user_id: u.id, ...payload }, { headers }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const dipendenti = users.filter((u) => u.ruolo === "dipendente");
  const amministrativi = users.filter((u) => u.ruolo === "amministrativo");
  const isTitolare = user?.ruolo === "titolare";

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6" data-testid="reparti-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Building2 className="h-6 w-6 text-blue-600" /> Reparti</h1>
          <p className="text-sm text-slate-500 mt-1">Suddividi i dipendenti in reparti, decidi cosa vedono nel calendario e quali amministrativi seguono ciascun reparto.</p>
        </div>

        {loading ? <Loader2 className="h-6 w-6 animate-spin text-slate-400" /> : (
          <>
            <Card data-testid="reparti-list-card">
              <CardHeader><CardTitle className="text-base">Reparti ({reparti.length})</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {reparti.map((r) => (
                  <div key={r.id} className="flex flex-col sm:flex-row sm:items-center gap-2 border border-slate-200 rounded-lg p-3" data-testid={`reparto-row-${r.id}`}>
                    <Input defaultValue={r.nome} onBlur={(e) => e.target.value.trim() && e.target.value !== r.nome && aggiorna(r, { nome: e.target.value.trim() })} className="sm:max-w-xs font-medium" data-testid={`reparto-nome-${r.id}`} />
                    <span className="text-xs text-slate-500 whitespace-nowrap"><Users className="inline h-3.5 w-3.5 mr-1" />{r.num_dipendenti} dip.</span>
                    <div className="flex items-center gap-2 sm:ml-auto">
                      <label className="text-xs text-slate-500">Calendario:</label>
                      <select value={r.visibilita} onChange={(e) => aggiorna(r, { visibilita: e.target.value })} className="border border-slate-200 rounded-md h-9 px-2 text-sm bg-white" data-testid={`reparto-vis-${r.id}`}>
                        {VIS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                      {isTitolare && (
                        <button onClick={() => elimina(r)} className="text-red-500 hover:text-red-700 p-1" title="Elimina reparto" data-testid={`reparto-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>
                      )}
                    </div>
                  </div>
                ))}
                {reparti.length === 0 && <p className="text-sm text-slate-500">Nessun reparto ancora. Creane uno qui sotto (es. Officina, Ufficio).</p>}
                <div className="flex gap-2 pt-2">
                  <Input value={nuovo} onChange={(e) => setNuovo(e.target.value)} placeholder="Nome nuovo reparto" onKeyDown={(e) => e.key === "Enter" && crea()} data-testid="reparto-nuovo-input" />
                  <Button onClick={crea} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="reparto-crea-btn"><Plus className="h-4 w-4 mr-1" /> Crea</Button>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="reparti-dipendenti-card">
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4 text-emerald-600" /> Dipendenti e reparto</CardTitle></CardHeader>
              <CardContent className="divide-y divide-slate-100">
                {dipendenti.map((u) => (
                  <div key={u.id} className="flex items-center justify-between gap-3 py-2" data-testid={`dip-reparto-row-${u.id}`}>
                    <span className="text-sm text-slate-800">{u.cognome} {u.nome}</span>
                    <select value={u.reparto_id || ""} onChange={(e) => assegna(u, { reparto_id: e.target.value || null })} className="border border-slate-200 rounded-md h-9 px-2 text-sm bg-white" data-testid={`dip-reparto-select-${u.id}`}>
                      <option value="">— Nessun reparto —</option>
                      {reparti.map((r) => <option key={r.id} value={r.id}>{r.nome}</option>)}
                    </select>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card data-testid="reparti-amministrativi-card">
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-blue-600" /> Amministrativi e reparti seguiti</CardTitle></CardHeader>
              <CardContent className="divide-y divide-slate-100">
                {amministrativi.length === 0 && <p className="text-sm text-slate-500">Nessun amministrativo. Senza reparti associati un amministrativo vede tutta l'azienda.</p>}
                {amministrativi.map((u) => (
                  <div key={u.id} className="py-2" data-testid={`adm-reparti-row-${u.id}`}>
                    <p className="text-sm text-slate-800 mb-1">{u.cognome} {u.nome} <span className="text-xs text-slate-400">{(u.reparti_ids || []).length === 0 ? "— vede tutta l'azienda" : ""}</span></p>
                    <div className="flex flex-wrap gap-3">
                      {reparti.map((r) => {
                        const on = (u.reparti_ids || []).includes(r.id);
                        return (
                          <label key={r.id} className="flex items-center gap-1.5 text-sm">
                            <input type="checkbox" checked={on} onChange={() => assegna(u, { reparti_ids: on ? u.reparti_ids.filter((x) => x !== r.id) : [...(u.reparti_ids || []), r.id] })} data-testid={`adm-reparto-check-${u.id}-${r.id}`} />
                            {r.nome}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
}
